(function () {
  'use strict';

  /*
    ArcaVerse cart count fixer

    Goal:
    Shopify's normal cart.item_count counts every real cart line:
      1 box + 3 paid add-ons = 4

    Customer-facing count should count configured products:
      1 configured box = 1

    This script:
    - fetches /cart.js
    - ignores hidden ArcaVerse add-on lines
    - sums parent/normal item quantities
    - updates visible cart bubbles/counts after add/update/remove
  */

  var ADDON_VARIANT_IDS = {
    '51898142523688': true, // Custom Text
    '51898142556456': true, // Extra Color / Lid Color
    '51898142589224': true, // Image Upload
    '51898142621992': true, // Stock Icon
    '51898142654760': true  // Pattern
  };

  var isUpdatingCount = false;
  var lastConfiguredCount = null;

  function getProps(item) {
    return item && item.properties && typeof item.properties === 'object'
      ? item.properties
      : {};
  }

  function isArcaverseAddonLine(item) {
    var props = getProps(item);

    if (props._av_addon_key) {
      return true;
    }

    if (item && item.variant_id && ADDON_VARIANT_IDS[String(item.variant_id)]) {
      return true;
    }

    if (
      item &&
      item.product_title &&
      String(item.product_title).toLowerCase().indexOf('customization add-ons') !== -1 &&
      props._av_config_id
    ) {
      return true;
    }

    return false;
  }

  function getConfiguredCartCount(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }

    return cart.items.reduce(function (sum, item) {
      if (isArcaverseAddonLine(item)) {
        return sum;
      }

      return sum + Number(item.quantity || 0);
    }, 0);
  }

  function getRawCartCount(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }

    return cart.items.reduce(function (sum, item) {
      return sum + Number(item.quantity || 0);
    }, 0);
  }

  function setText(el, value) {
    if (!el) return;

    el.textContent = String(value);
  }

  function setHidden(el, shouldHide) {
    if (!el) return;

    el.classList.toggle('hidden', shouldHide);
    el.classList.toggle('visually-hidden', shouldHide && el.classList.contains('cart-bubble'));
  }

  function updateBubbleElements(configuredCount) {
    var shouldHide = configuredCount <= 0;
    var displayValue = configuredCount > 99 ? '99+' : String(configuredCount);

    /*
      Header cart bubble / drawer cart bubble
    */
    document.querySelectorAll('.cart-bubble').forEach(function (bubble) {
      bubble.classList.toggle('visually-hidden', shouldHide);
      bubble.classList.toggle('hidden', shouldHide);

      if (configuredCount <= 99) {
        bubble.setAttribute('data-maintain-ratio', '');
      } else {
        bubble.removeAttribute('data-maintain-ratio');
      }
    });

    document.querySelectorAll(
      '.cart-bubble__text-count, [data-testid="cart-bubble"], [ref="cartBubbleCount"]'
    ).forEach(function (countEl) {
      setText(countEl, displayValue);
      countEl.classList.toggle('hidden', shouldHide);
    });

    /*
      Horizon cart-products hidden count ref.
      This helps some theme JS know the customer-facing count.
    */
    document.querySelectorAll('[ref="cartItemCount"]').forEach(function (countEl) {
      setText(countEl, configuredCount);
    });

    /*
      Common badge/count fallbacks.
      These are intentionally conservative and mostly catch theme clones.
    */
    document.querySelectorAll(
      '[data-cart-count], [data-cart-count-bubble], .cart-count, .cart-count-bubble'
    ).forEach(function (countEl) {
      setText(countEl, displayValue);
      countEl.classList.toggle('hidden', shouldHide);
      countEl.classList.toggle('visually-hidden', shouldHide);
    });

    document.documentElement.style.setProperty('--av-configured-cart-count', String(configuredCount));
  }

  function updateCartAriaLabels(configuredCount) {
    var label = configuredCount === 1
      ? 'Cart, 1 item'
      : 'Cart, ' + configuredCount + ' items';

    document.querySelectorAll(
      'a[href="/cart"], a[href*="/cart"], button[aria-controls*="cart"], button[aria-label*="Cart"], summary[aria-label*="Cart"]'
    ).forEach(function (el) {
      var aria = el.getAttribute('aria-label');

      if (aria && aria.toLowerCase().indexOf('cart') !== -1) {
        el.setAttribute('aria-label', label);
      }
    });
  }

  function fetchCart() {
    return fetch('/cart.js', {
      headers: {
        Accept: 'application/json'
      }
    }).then(function (response) {
      return response.json();
    });
  }

  function updateConfiguredCartCount() {
    if (isUpdatingCount) {
      return Promise.resolve(lastConfiguredCount);
    }

    isUpdatingCount = true;

    return fetchCart()
      .then(function (cart) {
        var configuredCount = getConfiguredCartCount(cart);
        var rawCount = getRawCartCount(cart);

        lastConfiguredCount = configuredCount;

        updateBubbleElements(configuredCount);
        updateCartAriaLabels(configuredCount);

        document.dispatchEvent(
          new CustomEvent('arcaverse:cart-count-updated', {
            detail: {
              configuredCount: configuredCount,
              rawCount: rawCount,
              cart: cart
            }
          })
        );

        return configuredCount;
      })
      .catch(function (error) {
        console.warn('ArcaVerse cart count update failed:', error);
        return lastConfiguredCount;
      })
      .finally(function () {
        isUpdatingCount = false;
      });
  }

  function scheduleCountUpdate(delay) {
    window.setTimeout(function () {
      updateConfiguredCartCount();
    }, delay || 0);
  }

  function isCartEndpoint(url) {
    var text = String(url || '');

    return (
      text.indexOf('/cart/add') !== -1 ||
      text.indexOf('/cart/change') !== -1 ||
      text.indexOf('/cart/update') !== -1 ||
      text.indexOf('/cart/clear') !== -1
    );
  }

  /*
    Patch fetch so count updates after Ajax cart mutations.
  */
  if (window.fetch && !window.__arcaverseCartCountFetchPatched) {
    window.__arcaverseCartCountFetchPatched = true;

    var originalFetch = window.fetch;

    window.fetch = function () {
      var requestUrl = arguments[0];

      if (requestUrl && typeof requestUrl === 'object' && requestUrl.url) {
        requestUrl = requestUrl.url;
      }

      var cartMutation = isCartEndpoint(requestUrl);

      return originalFetch.apply(this, arguments).then(function (response) {
        if (cartMutation) {
          /*
            Give Horizon/Shopify time to render the updated cart,
            then refresh the customer-facing count.
          */
          scheduleCountUpdate(80);
          scheduleCountUpdate(350);
          scheduleCountUpdate(900);
        }

        return response;
      });
    };
  }

  /*
    Patch XMLHttpRequest too, just in case the theme/app uses XHR instead of fetch.
  */
  if (window.XMLHttpRequest && !window.__arcaverseCartCountXhrPatched) {
    window.__arcaverseCartCountXhrPatched = true;

    var originalOpen = window.XMLHttpRequest.prototype.open;
    var originalSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__avCartMutation = isCartEndpoint(url);
      return originalOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function () {
      if (this.__avCartMutation) {
        this.addEventListener('loadend', function () {
          scheduleCountUpdate(80);
          scheduleCountUpdate(350);
          scheduleCountUpdate(900);
        });
      }

      return originalSend.apply(this, arguments);
    };
  }

  /*
    Watch for cart drawer/cart bubble being re-rendered and reapply the count.
  */
  var observerStarted = false;

  function startObserver() {
    if (observerStarted || !document.body || !window.MutationObserver) return;

    observerStarted = true;

    var pending = false;

    var observer = new MutationObserver(function (mutations) {
      var shouldUpdate = false;

      mutations.forEach(function (mutation) {
        if (shouldUpdate) return;

        var nodes = Array.prototype.slice.call(mutation.addedNodes || []);

        nodes.forEach(function (node) {
          if (shouldUpdate || !node || node.nodeType !== 1) return;

          if (
            node.matches &&
            (
              node.matches('.cart-bubble') ||
              node.matches('.cart-drawer') ||
              node.matches('.cart-items__wrapper') ||
              node.matches('[ref="cartItemCount"]')
            )
          ) {
            shouldUpdate = true;
          }

          if (
            !shouldUpdate &&
            node.querySelector &&
            (
              node.querySelector('.cart-bubble') ||
              node.querySelector('.cart-drawer') ||
              node.querySelector('.cart-items__wrapper') ||
              node.querySelector('[ref="cartItemCount"]')
            )
          ) {
            shouldUpdate = true;
          }
        });
      });

      if (shouldUpdate && !pending) {
        pending = true;

        window.setTimeout(function () {
          pending = false;
          updateConfiguredCartCount();
        }, 120);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /*
    Initial + lifecycle updates.
  */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      updateConfiguredCartCount();
      startObserver();
    });
  } else {
    updateConfiguredCartCount();
    startObserver();
  }

  window.addEventListener('pageshow', function () {
    updateConfiguredCartCount();
  });

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) {
      updateConfiguredCartCount();
    }
  });

  document.addEventListener('cart:refresh', function () {
    updateConfiguredCartCount();
  });

  document.addEventListener('cart:update', function () {
    updateConfiguredCartCount();
  });

  document.addEventListener('theme:cart:update', function () {
    updateConfiguredCartCount();
  });

  document.addEventListener('arcaverse:cart-group-updated', function () {
    updateConfiguredCartCount();
  });

  /*
    Extra safety: when customer clicks quantity/remove/cart controls, recheck shortly after.
  */
  document.addEventListener('click', function (event) {
    var target = event.target;

    if (!target || !target.closest) return;

    if (
      target.closest('.quantity-selector') ||
      target.closest('.cart-items__remove') ||
      target.closest('[href="/cart"]') ||
      target.closest('[aria-label*="Cart"]')
    ) {
      scheduleCountUpdate(250);
      scheduleCountUpdate(800);
    }
  });
})();